/**
 * Socket.io 클라이언트 관리
 * 소켓 연결, 메시지 수신/전송, 재연결, 연결 상태 관리를 제공합니다.
 */

import {useSyncExternalStore, useCallback} from "react";
import {
  io,
  ManagerOptions,
  SocketOptions,
  type Socket as IOSocket,
} from "socket.io-client";

import {ConnectionState, type EventCallback, type RoomId} from "./socket.type";

class SocketManager {
  private socket: IOSocket | null = null;
  private connectionState: ConnectionState = ConnectionState.INIT;
  private eventListeners: Map<string, EventCallback[]> = new Map();
  private stateChangeCallbacks: Set<(state: ConnectionState) => void> =
    new Set();
  private rooms: Set<RoomId> = new Set();
  private roomChangeCallbacks: Set<() => void> = new Set();
  private cachedRooms: RoomId[] = [];

  connect = (
    url: string,
    options?: Partial<ManagerOptions & SocketOptions>,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      if (this.socket) {
        this.socket.disconnect();
      }

      this.updateConnectionState(ConnectionState.CONNECTING);

      this.socket = io(url, {
        reconnection: true,
        reconnectionDelay: 1000, // 1초마다 재시도
        reconnectionDelayMax: 5000, // 최대 5초
        reconnectionAttempts: 5, // 5번 시도
        transports: ["websocket", "polling"],
        ...options,
      });

      this.setupSocketListeners();

      const connectHandler = (): void => {
        this.socket?.off("connect", connectHandler);
        this.socket?.off("connect_error", errorHandler);
        resolve();
      };

      const errorHandler = (error: Error): void => {
        this.socket?.off("connect", connectHandler);
        this.socket?.off("connect_error", errorHandler);
        reject(error);
      };

      this.socket.on("connect", connectHandler);
      this.socket.on("connect_error", errorHandler);
    });
  };

  disconnect = (): void => {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.rooms.clear();
    this.cachedRooms = [];
    this.notifyRoomChange();
    this.updateConnectionState(ConnectionState.DISCONNECTED);
    this.clearAllListeners();
  };

  emit = (event: string, data: unknown): void => {
    if (!this.socket?.connected) {
      console.error("Socket is not connected");
      return;
    }

    this.socket.emit(event, data);
  };

  on = (event: string, callback: EventCallback): void => {
    if (!this.socket) {
      console.error("Socket is not initialized");
      return;
    }

    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }

    const listeners = this.eventListeners.get(event);
    if (listeners) {
      if (listeners.includes(callback)) {
        return;
      }
      listeners.push(callback);
    }

    this.socket.on(event, callback);
  };

  off = (event: string, callback?: EventCallback): void => {
    if (!this.socket) {
      return;
    }

    if (callback) {
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
        if (listeners.length === 0) {
          this.eventListeners.delete(event);
        }
      }
      this.socket.off(event, callback);
    } else {
      this.eventListeners.delete(event);
      this.socket.off(event);
    }
  };

  join = (roomId: RoomId): void => {
    if (!this.socket?.connected) {
      console.error("Socket is not connected");
      return;
    }

    if (this.rooms.has(roomId)) {
      return;
    }

    this.socket.emit("join", roomId);
    this.rooms.add(roomId);
    this.notifyRoomChange();
  };

  leave = (roomId: RoomId): void => {
    if (!this.rooms.has(roomId)) {
      return;
    }

    if (!this.socket?.connected) {
      console.error("Socket is not connected");
      return;
    }

    this.socket.emit("leave", roomId);
    this.rooms.delete(roomId);
    this.notifyRoomChange();
  };

  getRooms = (): RoomId[] => {
    return this.cachedRooms;
  };

  // 내부 사용
  subscribeRooms = (onStoreChange: () => void): (() => void) => {
    const callback = () => {
      onStoreChange();
    };

    this.roomChangeCallbacks.add(callback);

    return () => {
      this.roomChangeCallbacks.delete(callback);
    };
  };

  getConnectionState = (): ConnectionState => {
    return this.connectionState;
  };

  subscribe = (onStoreChange: () => void): (() => void) => {
    const callback = () => {
      onStoreChange();
    };

    this.stateChangeCallbacks.add(callback);

    return () => {
      this.stateChangeCallbacks.delete(callback);
    };
  };

  onStateChange = (
    callback: (state: ConnectionState) => void,
  ): (() => void) => {
    this.stateChangeCallbacks.add(callback);

    return () => {
      this.stateChangeCallbacks.delete(callback);
    };
  };

  private setupSocketListeners = (): void => {
    if (!this.socket) {
      return;
    }

    this.socket.off("connect");
    this.socket.off("disconnect");
    this.socket.off("connect_error");
    this.socket.off("reconnect");
    this.socket.off("reconnect_attempt");
    this.socket.off("reconnect_error");
    this.socket.off("reconnect_failed");

    this.socket.on("connect", () => {
      this.updateConnectionState(ConnectionState.CONNECTED);
    });

    this.socket.on("disconnect", () => {
      this.updateConnectionState(ConnectionState.DISCONNECTED);
    });

    this.socket.on("connect_error", (error: Error) => {
      console.error("Socket connection error:", error);
      this.updateConnectionState(ConnectionState.DISCONNECTED);
    });

    this.socket.on("reconnect", () => {
      this.updateConnectionState(ConnectionState.CONNECTED);
      this.reRegisterEventListeners();
      this.reRegisterRooms();
    });

    this.socket.on("reconnect_attempt", () => {
      this.updateConnectionState(ConnectionState.RECONNECTING);
    });

    this.socket.on("reconnect_error", (error: Error) => {
      console.error("Socket reconnection error:", error);
    });

    this.socket.on("reconnect_failed", () => {
      console.error("Socket reconnection failed");
      this.updateConnectionState(ConnectionState.DISCONNECTED);
    });
  };

  private removeAllEventListeners = (): void => {
    if (!this.socket) {
      return;
    }

    this.eventListeners.forEach((_, event) => {
      this.socket?.off(event);
    });
  };

  private updateConnectionState = (state: ConnectionState): void => {
    if (this.connectionState === state) {
      return;
    }

    this.connectionState = state;

    if (state === ConnectionState.DISCONNECTED) {
      this.removeAllEventListeners();
    }

    this.stateChangeCallbacks.forEach((callback) => {
      callback(state);
    });
  };

  private clearAllListeners = (): void => {
    this.eventListeners.clear();
  };

  private reRegisterEventListeners = (): void => {
    if (!this.socket) {
      return;
    }

    this.eventListeners.forEach((callbacks, event) => {
      this.socket?.off(event);
      callbacks.forEach((callback) => {
        this.socket?.on(event, callback);
      });
    });
  };

  private reRegisterRooms = (): void => {
    if (!this.socket?.connected) {
      return;
    }

    this.rooms.forEach((roomId) => {
      this.socket?.emit("join", roomId);
    });
  };

  private notifyRoomChange = (): void => {
    this.cachedRooms = Array.from(this.rooms);
    this.roomChangeCallbacks.forEach((callback) => {
      callback();
    });
  };
}

const socketManager = new SocketManager();

export const useSocket = () => {
  const connectionState = useSyncExternalStore(
    socketManager.subscribe,
    socketManager.getConnectionState,
    socketManager.getConnectionState,
  );

  const rooms = useSyncExternalStore(
    socketManager.subscribeRooms,
    socketManager.getRooms,
    socketManager.getRooms,
  );

  const connect = useCallback(
    async (url: string, options?: Partial<ManagerOptions & SocketOptions>) => {
      await socketManager.connect(url, options);
    },
    [],
  );

  const disconnect = useCallback(() => {
    socketManager.disconnect();
  }, []);

  const emit = useCallback((event: string, data: unknown) => {
    socketManager.emit(event, data);
  }, []);

  const on = useCallback((event: string, callback: EventCallback) => {
    socketManager.on(event, callback);
  }, []);

  const off = useCallback((event: string, callback?: EventCallback) => {
    socketManager.off(event, callback);
  }, []);

  const join = useCallback((roomId: RoomId) => {
    socketManager.join(roomId);
  }, []);

  const leave = useCallback((roomId: RoomId) => {
    socketManager.leave(roomId);
  }, []);

  return {
    connectionState,
    rooms,
    connect,
    disconnect,
    emit,
    on,
    off,
    join,
    leave,
  };
};
